import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { environment } from 'environments/environment';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ORGANISATION_PLACEHOLDER, REGISTER_PLACEHOLDER } from '../http-token.interceptor';
import { GuideStepModel } from '../models/onboarding/guide-step-model';
import { OnboardingGuideStatus } from '../models/onboarding/onboarding-guide-status';
import { OnboardingGuideType } from '../models/onboarding/onboarding-guide-type';
import { OrganisationOnboardingModel } from '../models/onboarding/onboarding-model';
import { OnboardingOrganisationResponse } from '../models/onboarding/onboarding-organisation-response';
import { CreateOrganisationOnboardingQuestionsCommand } from '../models/onboarding/onboarding-questions-command';
import { OnboardingRegisterGuideStepStatus } from '../models/onboarding/onboarding-register-guide-step-status';
import { OnboardingRegisterModel } from '../models/onboarding/onboarding-register-model';
import { OnboardingRegisterResponse } from '../models/onboarding/onboarding-register-response';
import { OnboardingSetupGuideStep } from '../models/onboarding/onboarding-setup-guide-step';
import { RegisterOnboardingStatus } from '../models/onboarding/register-onboarding';
import { setupGuideConfig } from '../models/onboarding/setup-guide-config';
import { AppNavigationService } from './app-navigation.service';
import { AuthorizationService } from './authorization.service';
import { TrackingService } from './tracking.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private orgOnboarding$: Observable<OrganisationOnboardingModel> | null;
  private registerOnboarding$: Observable<OnboardingRegisterModel> | null;
  private orgOnboardingId: number | null;
  private registerOnboardingId: number | null;
  private currentRegisterOnboarding: RegisterOnboardingStatus[] | null;
  private canCreateAsset$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  organisationOnboarding: BehaviorSubject<OrganisationOnboardingModel> = new BehaviorSubject(
    {} as OrganisationOnboardingModel,
  );

  registerOnboarding: BehaviorSubject<OnboardingRegisterModel> = new BehaviorSubject(
    {} as OnboardingRegisterModel,
  );

  showExpandedGuide: BehaviorSubject<boolean> = new BehaviorSubject(false as boolean);

  guideStatus$ = combineLatest([
    this.userService.getUserSettings(),
    this.registerOnboarding,
    this.canCreateAsset$,
  ]).pipe(
    map(([userSettings, onboarding, canCreateAsset]) => {
      if (!canCreateAsset) {
        return OnboardingGuideStatus.NotAvailable;
      }

      if (userSettings) {
        const isSetupGuideStarted =
          onboarding.setupGuide?.steps?.some(
            s =>
              s.stepId === OnboardingSetupGuideStep.CreateRegister &&
              s.status === OnboardingRegisterGuideStepStatus.Completed,
          ) ?? false;
        const isSetupGuideCompleted =
          onboarding.setupGuide?.steps?.some(
            s =>
              s.stepId === OnboardingSetupGuideStep.Completed &&
              s.status === OnboardingRegisterGuideStepStatus.Completed,
          ) ?? false;

        if (!isSetupGuideCompleted) {
          if (userSettings.showGuides && isSetupGuideStarted) {
            return OnboardingGuideStatus.Show;
          } else if (isSetupGuideStarted) {
            return OnboardingGuideStatus.Available;
          }
        }
      }
      return OnboardingGuideStatus.NotAvailable;
    }),
  );

  constructor(
    private appNavigationService: AppNavigationService,
    private http: HttpClient,
    private trackingService: TrackingService,
    private userService: UserService,
    private authorizationService: AuthorizationService,
  ) {
    this.appNavigationService.onOrganisationChanged.subscribe(() => {
      this.resetOrganisation();
    });
    this.appNavigationService.onRegisterChanged.subscribe(() => {
      this.registerOnboardingId = null;
      this.resetRegister();
    });
  }

  createOrganisationOnboardingQuestions(command: CreateOrganisationOnboardingQuestionsCommand) {
    return this.http.post(`${environment.servicesApiUrl}/api/v3/onboarding`, command);
  }

  /**
   * Toggle showing the guide in expanded or condensed mode
   *
   * @param expand boolean for whether the guide is expanded
   */
  expandGuide(expand: boolean) {
    this.showExpandedGuide.next(expand);
  }

  /**
   * Gets the organisation onboarding in state, or else makes API request
   *
   * @param organisationId specify the organisation
   */
  getOnboarding(organisationId?: number): Observable<OrganisationOnboardingModel> {
    if (!this.orgOnboarding$ || (organisationId && this.orgOnboardingId !== organisationId)) {
      this.orgOnboarding$ = this.getOrganisationOnboarding({ organisationId }).pipe(
        tap({
          error: () => {
            this.orgOnboarding$ = null;
          },
        }),
        map(response => {
          this.organisationOnboarding.next(response.organisationOnboarding);
          return response.organisationOnboarding;
        }),
        shareReplay(1),
      );
    }

    if (organisationId) {
      this.orgOnboardingId = organisationId;
    }
    return this.orgOnboarding$;
  }

  /**
   * Gets the register onboarding in state, or else makes API request
   *
   * @param registerId specify the register
   */
  getRegisterOnboarding(registerId?: number | null): Observable<OnboardingRegisterModel> {
    if (!this.registerOnboarding$ || (registerId && this.registerOnboardingId !== registerId)) {
      this.registerOnboarding$ = this.getRegisterOnboardingStatus({ registerId }).pipe(
        tap({
          error: () => {
            this.registerOnboarding$ = null;
          },
        }),
        map(response => {
          this.currentRegisterOnboarding = response.registerOnboardingStatus;

          const setupGuideSteps = this.mapStatusToStepConfig(response.registerOnboardingStatus);

          this.setRegisterOnboardingNextStep(setupGuideSteps);

          const onboardingRegister = {
            setupGuide: {
              steps: setupGuideSteps,
            },
          } as OnboardingRegisterModel;
          this.registerOnboarding.next(onboardingRegister);
          return onboardingRegister;
        }),
        shareReplay(1),
      );
    }

    if (registerId) {
      this.authorizationService
        .isRegisterAuthorized(registerId, RegisterPermissionType.CreateAsset)
        .subscribe(canCreateAsset => this.canCreateAsset$.next(canCreateAsset));

      this.registerOnboardingId = registerId;
    }

    return this.registerOnboarding$;
  }

  // Refetch register onboarding, bypassing the per-register cache, so new changes are displayed
  refreshRegisterOnboarding(registerId: number | null = null) {
    const targetRegisterId = registerId ?? this.registerOnboardingId;
    if (targetRegisterId == null) {
      return;
    }

    this.resetRegister();
    const sub = this.getRegisterOnboarding(targetRegisterId).subscribe(() => sub.unsubscribe());
  }

  /**
   * Clear the onboarding organsiation in state
   */
  resetOrganisation() {
    this.orgOnboarding$ = null;
    this.orgOnboardingId = null;
  }

  /**
   * Clear the register onboarding status in state
   */
  resetRegister() {
    this.registerOnboarding$ = null;
  }

  /**
   * Updates a step of the guide if the guide is active
   *
   * @param stepId the number of the step
   * @param setupStatus (optional) the status you want to update the step to - defaults to completed
   * @param guideType (optional) the guide you're updating - defaults to the Setup Guide
   */
  updateRegisterGuideStepStatus(
    stepId: number,
    setupStatus: OnboardingRegisterGuideStepStatus = OnboardingRegisterGuideStepStatus.Completed,
    guideType: OnboardingGuideType = OnboardingGuideType.SetupGuide,
  ) {
    if (
      this.currentRegisterOnboarding?.find(
        s => s.stepId === OnboardingSetupGuideStep.CreateRegister && s.guideType === guideType,
      )?.setupStatus === OnboardingRegisterGuideStepStatus.Completed
    ) {
      this.updateRegisterOnboardingStatus({
        stepId,
        setupStatus,
        guideType,
        registerId: this.registerOnboardingId,
      }).subscribe(() => {
        this.resetRegister();
        const sub = this.getRegisterOnboarding().subscribe(() => {
          sub.unsubscribe();
        });
      });

      let stepName: string | undefined = '';
      if (guideType === OnboardingGuideType.SetupGuide) {
        stepName = setupGuideConfig.find(s => s.stepId === stepId)?.name;
      }
      this.trackingService.trackEvent(
        `${OnboardingGuideType[guideType]}-${OnboardingRegisterGuideStepStatus[setupStatus]}-step-${stepId}`,
        stepName,
      );
    }
  }

  /**
   * Triggers the setup guide to display by setting the first step of the guide as complete
   */
  startRegisterSetupGuide(registerId: number | null = null) {
    this.updateRegisterOnboardingStatus({
      stepId: OnboardingSetupGuideStep.CreateRegister,
      setupStatus: OnboardingRegisterGuideStepStatus.Completed,
      guideType: OnboardingGuideType.SetupGuide,
      registerId: registerId || this.registerOnboardingId,
    }).subscribe(() => {
      this.resetRegister();
      const sub = this.getRegisterOnboarding(registerId).subscribe(() => {
        sub.unsubscribe();
      });
    });
  }

  private mapStatusToStepConfig(registerStatus: RegisterOnboardingStatus[]) {
    return setupGuideConfig.map(step => ({
      ...step,
      status:
        registerStatus.find(
          s => s.guideType === OnboardingGuideType.SetupGuide && s.stepId === step.stepId,
        )?.setupStatus ?? step.status,
    }));
  }

  private setRegisterOnboardingNextStep(setupGuideSteps: GuideStepModel[]) {
    const nextStep = setupGuideSteps.find(
      s =>
        s.status === OnboardingRegisterGuideStepStatus.NotStarted ||
        s.status === OnboardingRegisterGuideStepStatus.Next,
    );

    if (nextStep) {
      nextStep.status = OnboardingRegisterGuideStepStatus.Next;

      if (
        nextStep.stepId === OnboardingSetupGuideStep.MapGlAccounts &&
        setupGuideSteps.find(s => s.stepId === OnboardingSetupGuideStep.ConnectIntegration)
          ?.status === OnboardingRegisterGuideStepStatus.Skipped
      ) {
        // Skip GL account mapping if Integration skipped
        this.updateRegisterGuideStepStatus(
          OnboardingSetupGuideStep.MapGlAccounts,
          OnboardingRegisterGuideStepStatus.Skipped,
          OnboardingGuideType.SetupGuide,
        );
      }
    }
  }

  private getOrganisationOnboarding({
    organisationId,
  }: {
    organisationId?: number;
  } = {}): Observable<OnboardingOrganisationResponse> {
    return this.http.get<OnboardingOrganisationResponse>(
      `${environment.servicesApiUrl}/api/v3/onboarding/${
        organisationId || ORGANISATION_PLACEHOLDER
      }`,
    );
  }

  private getRegisterOnboardingStatus({
    registerId: registerId,
  }: {
    registerId?: number | null;
  } = {}): Observable<OnboardingRegisterResponse> {
    return this.http.get<OnboardingRegisterResponse>(
      `${environment.servicesApiUrl}/api/v3/onboarding/register/${
        registerId || REGISTER_PLACEHOLDER
      }`,
    );
  }

  private updateRegisterOnboardingStatus(command: RegisterOnboardingStatus): Observable<void> {
    return this.http.put<void>(
      `${environment.servicesApiUrl}/api/v3/onboarding/register/${
        command.registerId || REGISTER_PLACEHOLDER
      }`,
      command,
    );
  }
}
